import type { SchemaPathTree } from '@angular/forms/signals';
import {
  applyEach,
  max,
  maxLength,
  min,
  minLength,
  required,
} from '@angular/forms/signals';
import type {
  FactoryEdit,
  FactoryEditPayload,
  FactoryForm,
} from '@indutrust/factory-model';
import {
  contactFormSchema,
  isoMaxDateValidator,
  noWhitespaceValidator,
} from '@indutrust/shared-util';
import {
  formatWebsiteForApi,
  stripUrlProtocol,
} from './strip-url-protocol.util';
import { workshopAreaValidator } from './workshop-area-validator';

function toNumberOrNull(
  value: number | string | null | undefined
): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Returns an initial {@link FactoryForm} value, optionally seeded from an
 * existing {@link FactoryEdit}.
 *
 * Replaces the previous `createFactoryFormGroup` factory. Used as the initial
 * value of the factory edit form's data signal.
 */
// eslint-disable-next-line complexity
export function createFactoryFormValue(
  factory?: Partial<FactoryEdit>
): FactoryForm {
  return {
    id: factory?.id ?? 0,
    slug: factory?.slug ?? '',
    name: factory?.name ?? '',
    status: factory?.status ?? 'draft',
    verified: factory?.verified ?? false,
    created_at: factory?.created_at ?? new Date().toISOString(),
    updated_at: factory?.updated_at ?? new Date().toISOString(),
    country_id: factory?.country_id ?? null,
    industrial_area_id: factory?.industrial_area_id ?? null,
    industrial_area_name: factory?.industrial_area_name ?? null,
    industrial_subtypes: factory?.industrial_subtypes ?? null,
    industrial_subtype_ids: factory?.industrial_subtype_ids ?? null,
    capabilities: factory?.capabilities ?? null,
    capability_ids_array: factory?.capability_ids_array ?? null,
    capability_ids: factory?.capability_ids ?? null,
    address: factory?.address ?? '',
    po_box: factory?.po_box ?? null,
    latitude: factory?.latitude ?? null,
    longitude: factory?.longitude ?? null,
    description: factory?.description ?? '',
    creation_date: factory?.creation_date ?? new Date().toISOString(),
    website: stripUrlProtocol(factory?.website ?? ''),
    linked_in_url: factory?.linked_in_url ?? null,
    project_ref_on_demand: factory?.project_ref_on_demand ?? false,
    turnover: factory?.turnover ?? null,
    turnover_on_demand: factory?.turnover_on_demand ?? false,
    company_size: factory?.company_size ?? '',
    headcount_production: toNumberOrNull(factory?.headcount_production),
    headcount_management: toNumberOrNull(factory?.headcount_management),
    workshop_used: factory?.workshop_used ?? null,
    workshop_available: factory?.workshop_available ?? null,
    trade_license_number: factory?.trade_license_number ?? null,
    certification: factory?.certification ?? [],
    logo: factory?.logo ?? null,
    images: factory?.images ?? [],
    current_capabilities: factory?.current_capabilities ?? [],
    shareholder_uae: factory?.shareholder_uae ?? null,
    product_reference: factory?.product_reference ?? null,
    activity_reference: factory?.activity_reference ?? null,
    contact_ids: factory?.contacts?.map((contact) => contact.id) ?? [],
    prospects:
      factory?.prospects?.map((prospect) => ({
        type: prospect.type,
        first_name: prospect.first_name ?? '',
        last_name: prospect.last_name ?? '',
        email: prospect.email,
        number: prospect.number,
        has_whatsapp: prospect.has_whatsapp,
        indutrustMainPoc: prospect.indutrustMainPoc,
        country_id: prospect.country_id,
        country_code: prospect.country_code,
      })) ?? [],
  };
}

/**
 * Schema fragment applying the factory edit form's validation rules.
 *
 * Wires the cross-field `workshopAreaValidator`, the `noWhitespaceValidator`
 * on the factory name, the hard-required fields for submission to
 * admin review per `libs/factory/SPEC.md` Q5 (legal name, country,
 * industrial area, address, latitude, longitude), and runs the contact
 * form schema on every entry of the `prospects` array.
 *
 * The supplier can still **save** an in-progress draft with missing
 * required fields — the editor's action bar only gates the Save button
 * on `formDirty()` and not on `formValid()`. The Submit-for-approval
 * button is gated on `formValid()`, which is what these `required(...)`
 * calls drive.
 *
 * @example
 * const factoryForm = form(factoryModel, factoryFormSchema);
 */
export function factoryFormSchema(p: SchemaPathTree<FactoryForm>): void {
  // Hard-required fields for submission (supplier can save a draft without them)
  required(p.name);
  required(p.country_id);
  required(p.industrial_area_id);
  required(p.address);
  required(p.latitude);
  required(p.longitude);

  // Name constraints
  noWhitespaceValidator(p.name);
  minLength(p.name, 2);
  maxLength(p.name, 200);

  // Text field length limits
  maxLength(p.address, 500);
  maxLength(p.description, 2000);
  maxLength(p.website, 500);

  // Numeric fields — cannot be negative
  min(p.headcount_management, 0);
  min(p.headcount_production, 0);
  min(p.workshop_used, 0);
  min(p.workshop_available, 0);
  min(p.turnover, 0);

  // Creation year cannot be in the future (ISO string; built-in maxDate needs Date)
  isoMaxDateValidator(p.creation_date, () => new Date());

  // Coordinate ranges
  min(p.latitude, -90);
  max(p.latitude, 90);
  min(p.longitude, -180);
  max(p.longitude, 180);

  // Cross-field: available workshop area must not exceed total
  workshopAreaValidator(p);

  // Contact sub-form validation
  applyEach(p.prospects, contactFormSchema);
}

/**
 * Serializes a {@link FactoryForm} value into a payload suitable for the
 * update/create API.
 *
 * Mirrors the previous `getFactoryEditPayload` shape — defaults
 * `industrial_subtype_ids` to an empty array when missing.
 *
 * @param value The current form value as returned by `form().value()`.
 */
export function getFactoryEditPayload(
  value: FactoryForm
): Partial<FactoryEditPayload> {
  return {
    name: value.name,
    industrial_area_id: value.industrial_area_id,
    longitude: value.longitude,
    latitude: value.latitude,
    address: value.address || null,
    description: value.description || null,
    creation_date: value.creation_date,
    trade_license_number: value.trade_license_number,
    website: formatWebsiteForApi(value.website),
    certification: value.certification.length > 0 ? value.certification : null,
    shareholder_uae: value.shareholder_uae,
    turnover: value.turnover,
    product_reference: value.product_reference,
    activity_reference: value.activity_reference,
    industrial_subtype_ids: value.industrial_subtype_ids ?? [],
    capability_ids: value.capability_ids ?? [],
    country_id: value.country_id,
    workshop_used: value.workshop_used,
    workshop_available: value.workshop_available,
    headcount_production: value.headcount_production,
    headcount_management: value.headcount_management,
    project_ref_on_demand: value.project_ref_on_demand,
    turnover_on_demand: value.turnover_on_demand,
  };
}
